/* eslint-disable max-len */
import { calcularImporteNetoIntermediario, getConvertedImporte } from "../../utils";
import { getImporteFacturable } from "../../utils/getImporteFacturable";
import { resolverPorcentajeApx } from "../../utils/resolverPorcentajeApx";

/**
 * Derivación por fila del tab "ventas" de VentasFacturas: FUENTE ÚNICA de los
 * montos que muestra la tabla (VENTA, COMISIÓN %, COMISIÓN $, NETO) y de los
 * escalares ordenables que consumirá el sort (Fase 3). Reúne en un solo lugar
 * lo que antes vivía inline en el render (reglas 10.1 / 10.19 / 10.27).
 *
 * IMPORTANTE — byte a byte: el bloque de cálculo de los valores MOSTRADOS está
 * copiado verbatim del render anterior (misma secuencia de operaciones y mismos
 * motores: getConvertedImporte + getRateForOportunidad para la conversión a
 * local por la fecha del documento —regla 10.185—, getImporteFacturable,
 * calcularImporteNetoIntermediario, resolverPorcentajeApx). No se "simplifica"
 * la aritmética (p.ej. no se colapsa `a/rate + b/rate` a `(a+b)/rate`) para no
 * mover ni un ULP respecto de lo que ya se veía en pantalla.
 *
 * Los escalares "*Ordenable" son SIEMPRE en moneda local (independientes de
 * `filtroMoneda`): el orden debe ser consistente aunque la vista esté en US$.
 *
 * @param {object} oportunidad - Fila cruda ya resuelta por useVFFiltering.
 * @param {object} ctx
 * @param {Array}    ctx.clientes         - Clientes del país (array).
 * @param {Array}    ctx.intermediarios   - Intermediarios del país (array).
 * @param {string}   ctx.pais
 * @param {object}   ctx.pctMap           - Mapa de % congelados (foto).
 * @param {string}   ctx.filtroMoneda     - "USD" | "LOCAL" (modo de visualización).
 * @param {Function} ctx.getRateForOportunidad - (op) => rate del dólar por fecha.
 * @param {Function} ctx.getDolarRateForDate   - (fecha) => rate del dólar.
 * @param {Function} ctx.fechaLocal       - (fechaISO) => Date sin shift de TZ.
 * @param {string|null} ctx.fechaEmisionSII - fecha_emision de la vinculación SII (o null).
 * @param {boolean}  ctx.tieneVincSII     - true si la fila tiene vinculación SII.
 * @param {boolean}  ctx.cobrada          - flag `cobrada` de getEstadoCobro.
 * @returns {object} display + escalares ordenables.
 */
export function derivarEscalaresVentaFila(oportunidad, ctx) {
  const {
    clientes,
    intermediarios,
    pais,
    pctMap,
    filtroMoneda,
    getRateForOportunidad,
    getDolarRateForDate,
    fechaLocal,
    fechaEmisionSII,
    tieneVincSII,
    cobrada,
  } = ctx;

  const {
    id,
    type,
    contrato_id,
    mes,
    ano,
    comision,
    descuento_acuerdo_pct,
    apx_id,
    client_id,
    facturafecha,
    facturafechacobro,
    plazocobro,
  } = oportunidad;

  // ─── Bloque de cálculo COPIADO VERBATIM del render (byte a byte idéntico) ───
  const importeLocal = getConvertedImporte(oportunidad, getRateForOportunidad(oportunidad));
  // Facturable (7.22): con descuento de acuerdo (OMNET 25%) la base es importe×(1−desc); sin
  // descuento → = importeLocal. VENTA/COMISIÓN/NETO se computan sobre el facturable (igual que
  // los totales de useVFFiltering) → coherentes con el modelo descuento y con el pie de tabla.
  const facturableLocal = getImporteFacturable({ ...oportunidad, importe: importeLocal });
  const dolarRate = getDolarRateForDate(facturafecha);
  const importeDisplay = filtroMoneda === "USD" ? facturableLocal / dolarRate : facturableLocal;
  // A3 (7.22): id compatible con keyDeFactura para leer el % congelado (foto). Contrato-mes
  // → "contrato-{id}-{MM-YYYY}" (c:{id}|{mes}); sales/pauta → id de venta (s:{id}).
  const keyDeFacturaId = type === "contrato" ? `contrato-${contrato_id}-${mes}-${ano}` : id;
  const netoLocal = calcularImporteNetoIntermediario(
    {
      id: keyDeFacturaId,
      importe: importeLocal,
      descuento_acuerdo_pct,
      facturafecha,
      client_id,
    },
    Object.values(clientes),
    Object.values(intermediarios),
    pais,
    pctMap
  );
  // Comisión = facturable − neto. Con descuento la guarda hace neto=facturable → comisión 0
  // (OMNET no paga comisión de agencia: el 25% es descuento, no comisión). Sin descuento,
  // facturable=importeLocal → idéntico a hoy.
  const comisionLocal = facturableLocal - netoLocal;
  const comisionDisplay = filtroMoneda === "USD" ? comisionLocal / dolarRate : comisionLocal;
  // APX (Publicis): capa aditiva paralela a la comisión de agencia. El % efectivo
  // mostrado = agencia% + apx%; el monto/neto se componen en el display (sin tocar
  // el motor de %). Fail-safe: apx_id null → apxPct=0 → fila idéntica a las no-APX.
  // % desde la fuente única (mini-unify A3b-4); resuelve por id igual que getApxMonto.
  const apxPct = resolverPorcentajeApx({ id: keyDeFacturaId, apx_id }, intermediarios, pctMap);
  const apxLocal = importeLocal * (apxPct / 100);
  const apxDisplay = filtroMoneda === "USD" ? apxLocal / dolarRate : apxLocal;
  const comisionConApx = comisionDisplay + apxDisplay;
  const netoDisplay = importeDisplay - comisionConApx;

  // VENTA se muestra en moneda NATIVA (US$ o local), sin convertir — igual que hoy.
  const ventaNativa = getImporteFacturable(oportunidad);
  // COMISIÓN %: idéntico al display actual; el `.toFixed(1)` queda en el JSX.
  const comisionPct = (comision || 0) + apxPct;

  // ─── Escalares ordenables — SIEMPRE en moneda local (regla 10.185) ───
  const ventaOrdenable = facturableLocal;
  const comisionPctOrdenable = comisionPct;
  const comisionMontoOrdenable = comisionLocal + apxLocal; // comisionConApx en local
  const netoOrdenable = facturableLocal - (comisionLocal + apxLocal); // netoDisplay en local

  // FECHA FACTURA ordenable: timestamp real subyacente (facturafecha → fecha_emision SII);
  // para las filas "AGO/26" sin factura, se deriva de mes+año (día 1). NUNCA del string mostrado.
  const displayFechaFact = facturafecha || fechaEmisionSII || null;
  let fechaFacturaTs = null;
  if (displayFechaFact) {
    const d = fechaLocal(displayFechaFact);
    fechaFacturaTs = d ? d.getTime() : null;
  } else if (mes && ano) {
    fechaFacturaTs = new Date(Number(ano), Number(mes) - 1, 1).getTime();
  }

  // COBRO ordenable: clave compuesta = `cobroCobrada` (bool) + `cobroOrdenable` (ts de la fecha
  // real de cobro/plazo). Los estados-texto ("Registrar"/"Sin facturar") no tienen fecha → ts
  // null → los toma el `nulls` del hook en Fase 3. Espeja las ramas de getEstadoCobro:
  //   cobrada / cobro futuro → facturafechacobro ; facturada con plazo → plazocobro.
  const tieneFactura = !!facturafecha || tieneVincSII;
  let cobroOrdenable = null;
  if (facturafechacobro) {
    const d = fechaLocal(facturafechacobro);
    cobroOrdenable = d ? d.getTime() : null;
  } else if (tieneFactura && plazocobro) {
    const d = fechaLocal(plazocobro);
    cobroOrdenable = d ? d.getTime() : null;
  }

  return {
    // Valores MOSTRADOS (consumidos por el JSX; byte a byte idénticos a antes).
    ventaNativa,
    comisionPct,
    comisionConApx,
    netoDisplay,
    apxPct,
    // Escalares ORDENABLES (inertes en Fase 2; los conecta el sort en Fase 3).
    ventaOrdenable,
    comisionPctOrdenable,
    comisionMontoOrdenable,
    netoOrdenable,
    fechaFacturaTs,
    cobroOrdenable,
    cobroCobrada: cobrada,
  };
}
